import { useApolloClient, useLazyQuery, useQuery } from "@apollo/client";
import { useState } from 'react';
import { type ApplicantCasesSectionLayoutContainerApplicantUserFieldsFragment, type ApplicantCasesSectionLayoutContainerCredentialVerificationCaseV1FieldsFragment, ApplicantCasesActiveCaseListContainerCredentialVerificationCasesV1ByApplicantQueryDocument, ApplicantCasesSectionLayoutContainerCredentialVerificationCaseV1CurrentDocument, ApplicantSectionLayoutContainerSendReportCaseV1CurrentDocument, ApplicantCasesActiveCaseListContainerSendReportCasesV1ByApplicantQueryDocument, type ApplicantCasesSectionLayoutContainerSendReportCaseV1FieldsFragment, ApplicantCasesSectionLayoutContainerApplicantUserCurrentDocument } from "../../../../generated";
import { ComponentQueryLoader } from "../../../shared/component-query-loader";
import { getItem } from "../../../shared/local-storage";
import { SectionLayout } from "./section-layout";
import { useNavigate } from "react-router-dom";

export const SectionLayoutContainer: React.FC = () => {
    const applicantUserId = getItem("applicantUserId");
    const client = useApolloClient();
    const navigate = useNavigate();

    const [credCaseLoading, setCredCaseLoading] = useState(false);
    const [credCaseError, setCredCaseError] = useState<Error>();

    const updateCredCaseCache = (newData: ApplicantCasesSectionLayoutContainerCredentialVerificationCaseV1FieldsFragment) => {
      client.cache.updateQuery(
        {
          query: ApplicantCasesActiveCaseListContainerCredentialVerificationCasesV1ByApplicantQueryDocument,
          variables: { applicantId: applicantUserId }
        },
        (existingData) => {
          if (!existingData) {
            return { credentialVerificationCasesV1ByApplicant: [newData] };
          }

          if(existingData.credentialVerificationCasesV1ByApplicant?.find((item) => item?.id === newData.id)) {
            return existingData;
          }

          return {
            ...existingData,
            credentialVerificationCasesV1ByApplicant: [newData, ...(existingData.credentialVerificationCasesV1ByApplicant ?? [])]
          };
        }
      );
    };

    const updateSendReportCaseCache = (newData: ApplicantCasesSectionLayoutContainerSendReportCaseV1FieldsFragment) => {
        client.cache.updateQuery(
          {
            query: ApplicantCasesActiveCaseListContainerSendReportCasesV1ByApplicantQueryDocument,
            variables: { applicantId: applicantUserId },
          },
          (existingData) => {
            if (!existingData) {
              return { sendReportCasesV1ByApplicant: [newData] };
            }

            if(existingData.sendReportCasesV1ByApplicant?.find((item) => item?.id === newData.id)) {
              return existingData;
            }
    
            return {
              ...existingData,
              sendReportCasesV1ByApplicant: [newData, ...(existingData.sendReportCasesV1ByApplicant ?? [])],
            };
          }
        );
    }

    const [createSendReportCase] = useLazyQuery(ApplicantSectionLayoutContainerSendReportCaseV1CurrentDocument, {
        variables: {
          applicantId: applicantUserId,
        },
        onCompleted: (newData) => {
          if (!newData.sendReportCaseV1Current) {
            return;
          }
          updateSendReportCaseCache(newData.sendReportCaseV1Current);
        },
      });

      const { data: applicantUserData, loading: applicantUserDataLoading, error: applicantUserDataError } = useQuery(ApplicantCasesSectionLayoutContainerApplicantUserCurrentDocument);

      const handleCreateSendReportCase = async () => {
        const { data } = await createSendReportCase();
        if (data?.sendReportCaseV1Current?.state === 'draft') {
          navigate(`send-report/${data?.sendReportCaseV1Current?.id}/draft`);
        }
    
        if (data?.sendReportCaseV1Current?.state !== 'draft') {
          navigate(`/applicant/cases/send-report/${data?.sendReportCaseV1Current?.id}/active`);
        }
      }
    
      const handleCreateCredentialCase = async () => {
        // Use client.query scoped here so it only runs on explicit user action.
        setCredCaseLoading(true);
        setCredCaseError(undefined);

        let currentCase: ApplicantCasesSectionLayoutContainerCredentialVerificationCaseV1FieldsFragment | null = null;
        try {
          const { data } = await client.query({
            query: ApplicantCasesSectionLayoutContainerCredentialVerificationCaseV1CurrentDocument,
            variables: { applicantId: applicantUserId },
            fetchPolicy: 'network-only',
          });

          currentCase = data?.credentialVerificationCaseV1Current ?? null;
        } catch (err: unknown) {
          setCredCaseError(err instanceof Error ? err : new Error(String(err)));
          setCredCaseLoading(false);
          return;
        }

        setCredCaseLoading(false);

        if (!currentCase) {
          return;
        }

        updateCredCaseCache(currentCase);

        if (currentCase.state === 'draft') {
          navigate(`credential-verification/${currentCase.id}/draft`);
        } else {
          navigate(`/applicant/cases/credential-verification/${currentCase.id}/active`);
        }
      };

    return (
        <ComponentQueryLoader
          loading={applicantUserDataLoading || credCaseLoading}
          error={applicantUserDataError || credCaseError}
          hasData={applicantUserData?.applicantUserCurrent}
          hasDataComponent={(
            <SectionLayout 
              applicantUserData={applicantUserData?.applicantUserCurrent as ApplicantCasesSectionLayoutContainerApplicantUserFieldsFragment}
              handleCreateCredentialCase={handleCreateCredentialCase}
              handleSendReportCase={handleCreateSendReportCase}
            />
          )} 
        />
    )

};
